import { applyLogic } from '@joyfill/conditional-logic';
import { joyDocHelper, tableHelper, tableFormulas } from '@builttocreate/engine-utils';
import FieldTypes from '../constants/FieldTypes';
import FileViews from '../constants/FileViews';
import { flattenArray } from './collectionHelper';
import { resolveColumnRequired, resolveFieldRequired } from './requiredLogic';
import { resolveColumnCellHidden } from './cellVisibilityLogic';
import { getFileViewSelector, sortFieldPositionsByXAndYCoordinates, isFieldHiddenInView } from './documentHelper';

const { getPageOrder, sortPages } = joyDocHelper;
const { sortColumns } = tableHelper;
const { getOrderedRows, getResolvedCellsByIndex } = tableFormulas;

/**
 * NO-2347 column formulas. Text columns are judged on their resolved value so a column formula
 * result counts as filled, a calculated "" counts as empty and "Error" counts as non-empty.
 * Required / hidden logic keeps reading the raw cells.
 *
 * @param {Object} cells          raw row cells
 * @param {Object} resolvedCells  display values for this row (getResolvedCellsByIndex entry)
 * @param {Array}  columns
 * @returns {Object}
 */
function getValidationCells(cells, resolvedCells, columns) {
  const next = { ...cells };
  (columns || []).forEach((col) => {
    if (col.type === 'text' && resolvedCells && col._id in resolvedCells) next[col._id] = resolvedCells[col._id];
  });
  return next;
}

/**
 * Resolve every row of a table field once, in the same row and column order the grid uses, and
 * return the results keyed by row OBJECT (never by id, so two physical rows sharing an id each
 * keep their own result).
 *
 * @param {Object} field
 * @returns {Map}
 */
function getResolvedCellsByRowObject(field) {
  const orderedRows = getOrderedRows(field.value, field.rowOrder);
  const orderedColumns = sortColumns(field.tableColumns || [], field.tableColumnOrder);
  const resolvedByIndex = getResolvedCellsByIndex(orderedRows, orderedColumns);
  return new Map(orderedRows.map((row, index) => [row, resolvedByIndex[index]]));
}

export const validate = (doc, options = {}) => {

  const view = options.view || FileViews.desktop;
  const defaultFile = doc?.files[0];
  const validated = { status: null, fieldValidations: [] };

  if (!defaultFile) return validated;

  /**
   * Step 1: Get the target file view from the document. Defaults to 'desktop' view.
   */
  const fileView = getFileViewSelector({ doc, view });

  const fieldValidations = [];

  /**
  * Step 2: Properly sort the pages and apply conditional logic to them.
  */
  applyLogic(
    sortPages(fileView.pages, getPageOrder(fileView.pageOrder, fileView.pages)),
    doc.fields,
    '_id'
  ).forEach((page) => {

    //2.1 Apply conditional logic to the page fields
    const logicAppliedFields = applyLogic(page.fields, doc.fields, '_id');

    //2.2 Sort page fields
    const pageFields = sortFieldPositionsByXAndYCoordinates(logicAppliedFields);

    //2.3 Validate each page field — hidden fields are excluded entirely
    pageFields.forEach((field) => {

      if (page.hidden || field.hidden || isFieldHiddenInView(field.hiddenViews, view)) {
        return;
      }

      const nextFieldValidation = validateField(field, view, doc.fields);
      nextFieldValidation.pageId = page._id;

      fieldValidations.push(nextFieldValidation);

    });

  });

  /**
   * Step 3: Check if all fields are valid to determine the overall status
   */
  const allFieldsValid = fieldValidations.every((validation) => validation.status === 'valid')

  return {
    status: allFieldsValid ? 'valid' : 'invalid',
    fieldValidations
  };

};

/**
 * valid: Field is not required OR Field is hidden OR Field value is populated.
 * invalid: Field is required AND Field is visible AND Field value is empty.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 *
 * @returns {{ field, status: 'valid' | 'invalid', reasons?: string[] }}
 *   - status 'valid'   → no `reasons` property
 *   - status 'invalid' → `reasons` array always present (may be empty)
 */
export function validateField(field, view, docFields) {

  if (field.hidden || isFieldHiddenInView(field.hiddenViews, view)) {
    return { field, status: 'valid' };
  }

  //Handle collection fields
  if (field.type === FieldTypes.collection) {
    const reasons = getCollectionFieldReasons(field, view, docFields);
    const { rowValidities } = getCollectionValidities(field, view, docFields);

    if (reasons.length > 0) {
      return { field, status: 'invalid', reasons, rowValidities };
    }
    return { field, status: 'valid', rowValidities };
  }

  //Handle table fields
  else if (field.type === FieldTypes.table) {
    const reasons = [];

    if (
      (resolveFieldRequired(field, docFields) && isEmptyValue(field.value, field.type)) ||
      isTableFieldInvalid(field, view, docFields)
    ) {
      reasons.push('Required');
    }

    const { rowValidities } = getTableValidities(field, view, docFields);

    if (reasons.length > 0) {
      return { field, status: 'invalid', reasons, rowValidities };
    }

    return { field, status: 'valid', rowValidities };
  }

  //Handle all other fields
  else {

    if (resolveFieldRequired(field, docFields) && isEmptyValue(field.value, field.type)) {
      return { field, status: 'invalid', reasons: ['Required'] };
    } else {
      return { field, status: 'valid' };
    }

  }

}

/**
 * Filter a column set down to the columns that participate in required validation:
 * apply conditional logic, then exclude deleted, hidden, and view-hidden columns.
 * This preserves the existing SDK contract that non-visible columns never fail validation.
 *
 * @param {Array} tableColumns
 * @param {String} view
 * @param {Array} docFields
 * @returns {Array}
 */
function getVisibleColumns(tableColumns, view, docFields) {
  if (!Array.isArray(tableColumns)) return [];
  const evaluated = docFields ? applyLogic(tableColumns, docFields, '_id') : tableColumns;
  return evaluated.filter((col) => {
    if (col.deleted) return false;
    if (col.hidden) return false;
    if (isFieldHiddenInView(col.hiddenViews, view)) return false;
    return true;
  });
}

/**
 * Build the cell validities for a single row against a set of visible columns using the
 * resolved required state (cellRequiredLogic -> requiredLogic -> static required).
 *
 * @param {Array} visibleColumns
 * @param {Object} cells
 * @param {Array} docFields
 * @returns {Array} cellsValidities for the row
 */
function buildRowCellValidities(visibleColumns, cells, docFields, validationCells = cells) {
  return visibleColumns.map((col) => {
    // A cell hidden by cellVisibilityLogic is exempt from required validation for this row,
    // mirroring how conditional-logic-hidden fields/columns are exempt.
    const hidden = resolveColumnCellHidden(col, cells, docFields);
    const required = !hidden && resolveColumnRequired(col, cells, docFields);
    const invalid = required && isEmptyCellValue(validationCells[col._id], col.type);
    return { status: invalid ? 'invalid' : 'valid', columnId: col._id };
  });
}

/**
 * Determine whether a single row is invalid because a visible, resolved-required column
 * has an empty cell. Uses the same visible-column gate + required resolution as validateField.
 *
 * @param {Array} tableColumns
 * @param {Object} cells
 * @param {String} view
 * @param {Array} docFields
 * @returns {Boolean}
 */
export function isRowInvalid(tableColumns, cells, view, docFields, resolvedCells) {
  const visibleColumns = getVisibleColumns(tableColumns, view, docFields);
  const rowCells = cells || {};
  const validationCells = resolvedCells ? getValidationCells(rowCells, resolvedCells, tableColumns) : rowCells;
  return visibleColumns.some(
    (col) => !resolveColumnCellHidden(col, rowCells, docFields)
      && resolveColumnRequired(col, rowCells, docFields)
      && isEmptyCellValue(validationCells[col._id], col.type)
  );
}

/**
 * Compute rowValidities for a table field. Each row carries its own cellsValidities.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {{ rowValidities: Array }}
 */
export function getTableValidities(field, view, docFields) {
  const { tableColumns, value: rows } = field;
  const visibleColumns = getVisibleColumns(tableColumns, view, docFields);
  const rowValidities = [];
  const resolvedByRow = getResolvedCellsByRowObject(field);

  (Array.isArray(rows) ? rows : []).forEach((row) => {
    if (row.deleted) return;
    const cells = row.cells || {};
    const cellsValidities = buildRowCellValidities(visibleColumns, cells, docFields, getValidationCells(cells, resolvedByRow.get(row), tableColumns));
    const status = cellsValidities.some((c) => c.status === 'invalid') ? 'invalid' : 'valid';
    rowValidities.push({ status, rowId: row._id, schemaId: null, cellsValidities });
  });

  return { rowValidities };
}

/**
 * Compute rowValidities for a collection field. Traverses the flattened rows so each nested
 * schema table's rows/cells are validated with the same visible-column gate and required
 * resolution as tables. Each row carries its own cellsValidities.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {{ rowValidities: Array }}
 */
export function getCollectionValidities(field, view, docFields) {
  const flattened = flattenArray(
    field.type,
    field.displayType,
    field.fieldPositionSchema,
    field.schema,
    field.value,
    field.disabled,
    field.mode,
    view,
    docFields
  );

  const rowValidities = [];

  flattened.forEach((item) => {
    if (item.type !== 'row') return;

    const schemaDef = item.schema || {};
    const cells = item.cells || {};
    const visibleColumns = getVisibleColumns(schemaDef.tableColumns, view, docFields);
    const cellsValidities = buildRowCellValidities(visibleColumns, cells, docFields, getValidationCells(cells, item.calculatedCells, schemaDef.tableColumns));

    // item.isInvalid covers required nested child tables that are empty.
    const status = (item.isInvalid || cellsValidities.some((c) => c.status === 'invalid'))
      ? 'invalid'
      : 'valid';

    rowValidities.push({
      status,
      rowId: item._id,
      schemaId: item.schemaKey,
      cellsValidities,
    });
  });

  return { rowValidities };
}

/**
 * Returns an array of reason strings describing why a collection field is invalid.
 * Empty array means the field is valid.
 *
 * Collection fields need special handling because they can have nested required
 * tables and columns — we must check all nested rows for missing required values,
 * not just the top-level required flag.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {string[]}
 */
function getCollectionFieldReasons(field, view, docFields) {
  const reasons = [];

  const requiredAndEmpty = resolveFieldRequired(field, docFields)
    && (!field.value || field.value?.length === 0 || field.value?.every((item) => item === ''));

  const flattenedArray = flattenArray(field.type, field.displayType, field.fieldPositionSchema, field.schema, field.value, field.disabled, field.mode, view, docFields);

  const invalidRowFound = flattenedArray.some((row) => row.isInvalid);

  if (requiredAndEmpty || invalidRowFound) {
    reasons.push('Required');
  }

  return reasons;
}

/**
 * Check if the collection field is empty.
 *
 * @param {Object} field
 * @param {String} view
 * @param {Array} docFields
 * @returns {Boolean}
 */
export function isCollectionFieldEmpty(field, view, docFields) {
  return getCollectionFieldReasons(field, view, docFields).length > 0;
}

/**
 * Check if a table field has any required columns with empty cell values.
 * 
 * A table is invalid if any visible required column has an empty cell in any row.
 * Columns that are hidden in the current view or deleted are skipped.
 * 
 * @param {Object} field - The table field object
 * @param {String} view - The current view (desktop, mobile, pdf)
 * @returns {Boolean} true if the table has required column violations
 */
export function isTableFieldInvalid(field, view, docFields) {

  const { tableColumns, value: rows } = field;

  if (!tableColumns || !Array.isArray(tableColumns)) return false;
  if (!rows || !Array.isArray(rows) || rows.length === 0) return false;

  const visibleColumns = getVisibleColumns(tableColumns, view, docFields);

  if (visibleColumns.length === 0) return false;

  const resolvedByRow = getResolvedCellsByRowObject(field);

  return rows.some((row) => {
    if (row.deleted) return false;
    const cells = row.cells || {};
    const validationCells = getValidationCells(cells, resolvedByRow.get(row), tableColumns);
    return visibleColumns.some((col) => {
      if (resolveColumnCellHidden(col, cells, docFields)) return false;
      if (!resolveColumnRequired(col, cells, docFields)) return false;
      return isEmptyCellValue(validationCells[col._id], col.type);
    });
  });

}

/**
 * Check if a table cell value is empty based on column type.
 *
 * @param {*} value
 * @param {String} columnType
 * @returns {Boolean}
 */
export function isEmptyCellValue(value, columnType) {
  if (columnType === 'image' || columnType === 'multiSelect') {
    return !Array.isArray(value) || value.length === 0 || value.every((item) => item === '');
  }
  return value === '' || value === null || value === undefined;
}

export function isEmptyValue(value, fieldType) {
  if (
    fieldType === FieldTypes.multiSelect ||
    fieldType === FieldTypes.image ||
    fieldType === FieldTypes.table ||
    fieldType === FieldTypes.file
  ) {
    return (
      !Array.isArray(value) ||
      value.length === 0 ||
      value.every((item) => item === '')
    );
  }
  return value === '' || value === null || value === undefined;
}

export default {
  validate,
  validateField,
  isTableFieldInvalid,
  isEmptyCellValue,
};
